// src/utils/jwtUtils.js
import { jwtDecode } from 'jwt-decode';
import { AUTH0_CLAIMS } from '../config/auth0.config';

/**
 * Decode JWT token and extract custom claims
 * @param {string} token - JWT token
 * @returns {Object} Decoded token with custom claims
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const decoded = jwtDecode(token);
    return decoded;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Extract role from JWT token
 * Backend stores roles in slug format (lowercase with hyphens)
 * This function converts them to uppercase constants for consistency
 * 
 * @param {string} token - JWT token
 * @returns {string|null} User role (USER, FARMER, ADMIN, SUPER_ADMIN)
 */
export const getRoleFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded) return null;
  
  const roleSlug = decoded[AUTH0_CLAIMS.ROLE] || null;
  if (!roleSlug) return null;
  
  // Convert slug to uppercase constant
  // 'user' → 'USER', 'farmer' → 'FARMER', 'super-admin' → 'SUPER_ADMIN'
  return roleSlug.toUpperCase().replace(/-/g, '_');
};

/**
 * Extract user ID from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} Auth0 user ID
 */
export const getUserIdFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded) return null;
  
  return decoded[AUTH0_CLAIMS.USER_ID] || decoded.sub || null;
};

/**
 * Extract email from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} User email
 */
export const getEmailFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded) return null;
  
  return decoded[AUTH0_CLAIMS.EMAIL] || decoded.email || null;
};

/**
 * Check if email is verified in JWT token
 * @param {string} token - JWT token
 * @returns {boolean} Email verification status
 */
export const isEmailVerifiedFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded) return false;
  
  return decoded[AUTH0_CLAIMS.EMAIL_VERIFIED] || decoded.email_verified || false;
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date
 */
export const getTokenExpiration = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  
  return new Date(decoded.exp * 1000);
};
